import { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import { Loader } from '@app/components';
import { changePassword, getAuthCode, SignInData } from '@app/api';
import { AxiosError } from 'axios';
import { IconArrowBack } from '@app/icons';
import Logo from './logo.png';
import { setMask } from '@app/helpers';
import { useForm } from 'forms/hooks';
import { ChangeUserPassword } from '@app/models';
import * as yup from 'yup';
import { t } from 'i18next';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { Card } from 'components/ui/card';

interface ISignInButton {
  active: boolean;
}

interface SignInFormProps {
  onSubmit: (data: SignInData) => Promise<boolean>;
  pending: boolean;
  onCurrentStepHandler: (step: number) => void;
  currentStep: number;
}

export interface UserFormData {
  pinCode: string;
  phone: string;
  repeatPassword: string;
  newPassword: string;
}

const RestorePassword: FC<SignInFormProps> = ({
  onSubmit,
  pending,
  onCurrentStepHandler,
  currentStep,
}) => {
  const [disabledBtnNext, setDisabledBtnNext] = useState(true);
  const [disabledBtRestore, setDisabledBtnRestore] = useState(true);
  const [step, setStep] = useState<number>(0);
  const [loader, setLoader] = useState<boolean>(false);
  const phoneRef = useRef<HTMLInputElement | null>(null);
  const [sendText, setSendText] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState('');
  const { values, onChange, errors, validate, setErrors } =
    useForm<UserFormData>({
      pinCode: '',
      phone: '',
      newPassword: '',
      repeatPassword: '',
    });

  const onCheckout = useCallback(async () => {
    try {
      setLoader(true);
      const data = await getAuthCode(values.phone);
      setLoader(false);
      if (!!data) {
        setStep(1);
        onChange('', 'pinCode');
        onChange('', 'newPassword');
        onChange('', 'repeatPassword');
        setShowErrorMessage('');
        setSendText(data.message);
        setErrors({
          repeatPassword: '',
        });
      }
    } catch (e) {
      const error = e as AxiosError<{
        message: string;
      }>;
      if (error.response) {
        const { message } = error.response.data;
        setErrors({
          phone: message,
        });
        setLoader(false);
      }
    }
  }, [values, setErrors, setStep, onChange]);

  const onSubmitForm = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      e.stopPropagation();

      try {
        const schema = yup.object().shape({
          pinCode: yup
            .string()
            .required(t('required', { ns: 'validation' })!)
            .min(4)
            .max(4),
          phone: yup
            .string()
            .required(t('required', { ns: 'validation' })!)
            .length(10),
          newPassword: yup
            .string()
            .required(t('required', { ns: 'validation' })!)
            .min(8, 'Пароль должен содержать 8 или более символов')
            .matches(/[a-z]/, 'Добавьте хотя бы одну строчную букву')
            .matches(/[A-Z]/, 'Добавьте хотя бы одну заглавную букву')
            .matches(/\d/, 'Добавьте хотя бы одну цифру')
            .matches(
              /[!@#$%^&*(),.?":{}|<>]/,
              'Добавьте хотя бы один специальный символ'
            ),
          repeatPassword: yup
            .string()
            .oneOf(
              [yup.ref('newPassword')],
              t('passwordConfirmation', { ns: 'validation' })!
            ),
        });

        const hasErrors = await validate(schema);

        if (hasErrors) {
          return;
        }

        const data: ChangeUserPassword = {
          user: {
            phone: values.phone,
            password: values.newPassword,
          },
          code: values.pinCode,
        };

        await changePassword(data);
        onSubmit({
          phone: values.phone,
          password: values.newPassword,
        });
      } catch (e) {
        const error = e as AxiosError<{
          message: string;
        }>;
        if (error.response) {
          const { message } = error.response.data;
          setShowErrorMessage(message);
        }
      }
    },
    [changePassword, onSubmit, values, validate]
  );
  useEffect(() => {
    if (currentStep === 2) {
      phoneRef.current?.focus();
    }
  }, [currentStep]);
  useEffect(() => {
    if (values.phone.length <= 9) {
      setDisabledBtnNext(true);
    } else {
      setDisabledBtnNext(false);
    }
    if (
      values.newPassword.length <= 0 ||
      values.repeatPassword.length <= 0 ||
      values.pinCode.length !== 4
    ) {
      setDisabledBtnRestore(true);
    } else {
      setDisabledBtnRestore(false);
    }
  }, [setDisabledBtnNext, setDisabledBtnRestore, values]);
  return (
    <div className="w-full h-full p-0">
      <div className="mb-6">
        {step === 0 ? (
          <Button
            variant="outline"
            className="flex items-center mb-8 px-2 py-4 w-[99px] h-10 text-primary rounded-lg justify-center"
            type="button"
            onClick={() => onCurrentStepHandler(0)}
          >
            <IconArrowBack className="mr-1" /> Назад
          </Button>
        ) : (
          <Button
            variant="outline"
            className="flex items-center mb-8 px-2 py-4 w-[99px] h-10 text-primary rounded-lg justify-center"
            type="button"
            onClick={() => setStep(0)}
          >
            <IconArrowBack className="mr-1" /> Назад
          </Button>
        )}
        <div className="flex items-center">
          <img src={Logo} className="w-[30px] h-[40px]" alt="logo" />
          <div className="flex flex-col ml-1">
            <h2 className="font-medium text-[21px] leading-[130%] m-0 p-0 text-black">
              SMR
            </h2>
            <p className="font-normal text-xs tracking-[0.25px] text-[#757575] m-0 p-0">
              Поиск подряда и поставщиков
            </p>
          </div>
        </div>
      </div>
      <div className="w-full">
        <span className="font-medium text-2xl text-[#424242] block mb-6">
          {step === 1 ? (
            <p className="text-base text-center m-0 p-0 leading-5 w-full">
              {sendText}
            </p>
          ) : (
            'Введите номер'
          )}
        </span>
        <form onSubmit={onSubmitForm}>
          <div className="my-6 flex flex-col gap-2">
            {step === 1 ? (
              <>
                <Input
                  type="text"
                  disabled={pending}
                  onChange={(e) => onChange(e.target.value, 'pinCode')}
                  value={values.pinCode}
                  name="pinCode"
                  placeholder="Пин-код"
                  maxLength={4}
                  className="mb-2"
                />
                {errors.pinCode && (
                  <span className="text-red-500 text-xs mt-1">
                    {errors.pinCode}
                  </span>
                )}
                <Input
                  type="password"
                  disabled={pending}
                  onChange={(e) => onChange(e.target.value, 'newPassword')}
                  value={values.newPassword}
                  name="newPassword"
                  placeholder="Новый пароль"
                  className="mb-2"
                />
                {errors.newPassword && (
                  <span className="text-red-500 text-xs mt-1">
                    {errors.newPassword}
                  </span>
                )}
                <Input
                  type="password"
                  disabled={pending}
                  onChange={(e) => onChange(e.target.value, 'repeatPassword')}
                  value={values.repeatPassword}
                  name="repeatPassword"
                  placeholder="Подтверждение пароля"
                  className="mb-2"
                />
                {errors.repeatPassword && (
                  <span className="text-red-500 text-xs mt-1">
                    {errors.repeatPassword}
                  </span>
                )}
                {showErrorMessage.length !== 0 ? (
                  <span className="text-red-500 text-xs mt-2">
                    {showErrorMessage}
                  </span>
                ) : null}
              </>
            ) : (
              <>
                <label htmlFor="phone" className="text-sm font-medium mb-1">
                  Номер телефона
                </label>
                <Input
                  id="phone"
                  type="text"
                  disabled={pending}
                  onChange={(e) => onChange(e.target.value, 'phone')}
                  value={values.phone}
                  name="phone"
                  placeholder="(777) 777 77 77"
                  ref={phoneRef}
                  className="mb-2"
                />
                {errors.phone && (
                  <span className="text-red-500 text-xs mt-1">
                    {errors.phone}
                  </span>
                )}
              </>
            )}
          </div>
          {step === 1 && (
            <Button
              className="w-full h-13 mt-6"
              disabled={disabledBtRestore || pending}
              type="submit"
            >
              {pending ? (
                <Loader size="small" color="primary" />
              ) : (
                'Восстановить'
              )}
            </Button>
          )}
        </form>
        <div className="flex flex-col mt-4">
          {step === 0 && (
            <Button
              onClick={onCheckout}
              className="w-full h-13 mt-6"
              disabled={disabledBtnNext || loader}
              type="button"
            >
              {loader ? <Loader size="small" color="primary" /> : 'Продолжить'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(RestorePassword);
